import PropTypes from "prop-types";
import React from "react";
import UseCSV from "@usecsv/react";
import { resolveToken } from "../../shared/marketing/themeTokens";
var _ = require('lodash');

const axios = require("axios").default;

export const FAN_IMPORT_CHUNK_SIZE = 250;
export const FAN_IMPORT_RETRY_HINT = "Import these rows again: importing a fan again updates them and won't create a duplicate.";

const CSVImporter = ({
  csrfToken,
  team,
  importerKey,
  type,
  renderAnchor,
  validateUrl,
  createUrl,
  onClose = () => {},
  additionalParams = {},
  dynamicColumns,
  chunkSize,
  retryHint,
}) => {
  const bulkDataFromRows = (rows) => {
    return rows.map((row) => {
      var attributeData = {};

      Object.keys(row.data).map((key) => {
        attributeData[key] = row.data[key].value;
      });

      return {
        index: row.row,
        data: attributeData
      }
    });
  };

  const validateRows = (uploadId, rows) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.post(validateUrl || ("/teams/" + team.id + "/csv_import_rows/bulk_validate"), {
      use_csv_upload_id: uploadId,
      type: type,
      csv_import_rows: {
        bulk: bulkDataFromRows(rows)
      },
      ...additionalParams,
    })
      .then(({ data }) => {
        return data;
      });
  };

  const validateRow = (uploadId, row) => {
    return validateRows(uploadId, [row]);
  };

  const savedCountSentence = (savedCount, totalCount) => {
    return `${savedCount.toLocaleString()} of ${totalCount.toLocaleString()} rows were confirmed saved.`;
  };

  const failedChunkMessage = (failureReason, savedCount, totalCount) => {
    const reason = (typeof failureReason === "string" && failureReason ? failureReason : "the request to Opendate failed").replace(/\.$/, "");

    return [
      `Not imported: ${reason}.`,
      savedCountSentence(savedCount, totalCount),
      "This row may or may not have been saved.",
      retryHint || "Check whether it was saved before importing it again.",
    ].join(" ");
  };

  const unsentChunkMessage = (savedCount, totalCount) => {
    return [
      "Not imported: the import stopped before this row was sent.",
      savedCountSentence(savedCount, totalCount),
      retryHint || "Import these rows again.",
    ].join(" ");
  };

  const postChunk = (uploadId, chunk) => {
    return axios.post(createUrl || ("/teams/" + team.id + "/csv_import_rows/bulk_create"), {
      use_csv_upload_id: uploadId,
      type: type,
      csv_import_rows: {
        bulk: chunk
      },
      ...additionalParams,
    })
      .then(({ data }) => (Array.isArray(data) ? { rowErrors: data } : { failureReason: null }))
      .catch((error) => ({ failureReason: error.response?.data?.error }));
  };

  const createRows = async (uploadId, rows) => {
    const bulkData = rows.map(({ row: index, ...data }) => ({ index: index, data: data }));
    const chunks = _.chunk(bulkData, chunkSize || bulkData.length);
    const errors = [];
    let savedCount = 0;

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    for (const [chunkIndex, chunk] of chunks.entries()) {
      const { rowErrors, failureReason } = await postChunk(uploadId, chunk);

      if (rowErrors === undefined) {
        const failedMessage = failedChunkMessage(failureReason, savedCount, bulkData.length);
        const unsentMessage = unsentChunkMessage(savedCount, bulkData.length);
        const unsentRows = chunks.slice(chunkIndex + 1).flat();

        return {
          errors: errors.concat(
            chunk.map((failedRow) => ({ row: failedRow.data, error: failedMessage })),
            unsentRows.map((unsentRow) => ({ row: unsentRow.data, error: unsentMessage })),
          )
        };
      }

      errors.push(...rowErrors);
      savedCount += chunk.length - rowErrors.length;
    }

    return { errors: errors };
  };

  const surface = resolveToken("--od-c-ffffff", "#FFFFFF");
  const text = resolveToken("--od-c-333333", "#333333");
  const textSecondary = resolveToken("--od-c-535862", "#535862");
  const border = resolveToken("--od-c-e9eaeb", "#E9EAEB");
  const brand = resolveToken("--od-c-1982c4", "#1982C4");
  const brandHover = resolveToken("--od-c-156ca2", "#156ca2");

  const primaryButton = {
    "backgroundColor": brand,
    "color": "#FFFFFF",
    "border": "none",
    "borderRadius": "8px",
    "boxShadow": "0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05)",
    "fontSize": "14px",
    "fontWeight": "600",
    ":hover": { "backgroundColor": brandHover, "color": "#FFFFFF" }
  };
  const secondaryButton = {
    "backgroundColor": surface,
    "color": resolveToken("--od-c-414651", "#414651"),
    "border": `1px solid ${resolveToken("--od-c-d5d7da", "#D5D7DA")}`,
    "borderRadius": "8px",
    "boxShadow": "0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05)",
    "fontSize": "14px",
    "fontWeight": "600",
    ":hover": { "backgroundColor": resolveToken("--od-c-fafafa", "#FAFAFA"), "color": resolveToken("--od-c-414651", "#414651") }
  };

  const useCSVTheme = {
    "customFont": [{
      "fontFamily": "Open Sans",
      "fontStyle": "normal",
      "fontWeight": "300 800",
      "fontDisplay": "swap",
      "srcURL": "https://fonts.gstatic.com/s/opensans/v44/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-mu0SC55I.woff2",
      "srcFormat": "woff2"
    }],
    "global": {
      "backgroundColor": surface,
      "primaryTextColor": text,
      "secondaryTextColor": resolveToken("--od-c-717680", "rgba(51,51,51,.5)"),
      "successColor": "#8AC926",
      "warningColor": "#FF595E"
    },
    "buttons": { "primary": primaryButton, "secondary": secondaryButton },
    "tables": {
      "headerColor": textSecondary,
      "headerBackgroundColor": resolveToken("--od-c-fafafa", "#FAFAFA"),
      "borderColor": border,
      "color": text
    },
    "steps": {
      "backgroundColor": surface,
      "activeBackgroundColor": brand,
      "completedBackgroundColor": brand,
      "color": textSecondary,
      "activeColor": "#FFFFFF",
      "completedColor": "#FFFFFF",
      "borderColor": border,
      "activeBorderColor": brand,
      "completedBorderColor": brand,
      "connectorColor": border,
      "titleColor": text
    },
    "cells": {
      "error": {
        "color": resolveToken("--sl-text-error", "#D92D20"),
        "backgroundColor": resolveToken("--sl-bg-error-primary", "#FEF3F2")
      },
      "info": {
        "color": brand,
        "backgroundColor": resolveToken("--sl-bg-brand-primary", "#F6FAFE")
      },
      "healing": {
        "color": resolveToken("--sl-text-success", "#079455"),
        "backgroundColor": resolveToken("--sl-bg-success-primary", "#ECFDF3")
      }
    },
    "modals": {
      "backgroundColor": surface,
      "color": text,
      "border": `1px solid ${border}`,
      "boxShadow": "0 12px 16px -4px rgba(10, 13, 18, 0.08), 0 4px 6px -2px rgba(10, 13, 18, 0.03)",
      "buttons": { "confirm": primaryButton, "cancel": secondaryButton }
    }
  };

  return (
    <UseCSV importerKey={importerKey}
      theme={useCSVTheme}
      dynamicColumns={dynamicColumns}
            onRecordsInitial={({uploadId, rows}) => validateRows(uploadId, rows)}
            onRecordEdit={({uploadId, row}) => validateRow(uploadId, row)}
      onData={(data) => createRows(data.uploadId, data.rows)}
            onClose={() => onClose() }
      render={(openModal) => renderAnchor(openModal)}
    />
  );
};

CSVImporter.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  importerKey: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  renderAnchor: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  dynamicColumns: PropTypes.arrayOf(PropTypes.object),
  chunkSize: PropTypes.number,
  retryHint: PropTypes.string,
};

export default CSVImporter;
